/**
 * #817 — the swipe rule for ItemDetail, mobile only.
 *
 * Geometry and exemptions are pure functions here; the touch plumbing is in
 * useSwipeNavigate.js. Same split as the other two: the rule is testable without a
 * touchscreen, and the thing that needs a real device is only the wiring.
 */

/** Minimum horizontal travel, px. Below this it is a tap or a jitter, not a swipe. */
export const SWIPE_MIN_PX = 60;
/** Maximum drift off horizontal, degrees. Above this the user meant to scroll the page. */
export const SWIPE_MAX_DRIFT_DEG = 30;

/**
 * Selectors whose subtree owns horizontal gestures. From the approved mockup's
 * swipe-exempt zone map: the tab strip, the title, focused inputs, the editor, and wide
 * tables all either scroll sideways themselves or would lose a caret drag.
 */
export const SWIPE_EXEMPT = [
  '.dtabs', '[role="tablist"]',
  '.titlerow',
  'input', 'textarea', 'select', '[contenteditable="true"]',
  '.mdxeditor', '.tox', '[data-testid="tinymce"]',
  '.scroll-x', 'table',
];

/**
 * Does the gesture qualify as a horizontal swipe?
 * @returns {'prev'|'next'|null}  'prev' for a rightward drag, 'next' for leftward
 */
export function swipeDirection(dx, dy) {
  const adx = Math.abs(dx);
  if (!Number.isFinite(adx) || adx < SWIPE_MIN_PX) return null;
  // atan2 of the drift against the travel, in degrees.
  const driftDeg = Math.abs(Math.atan2(Math.abs(dy) || 0, adx) * (180 / Math.PI));
  if (driftDeg >= SWIPE_MAX_DRIFT_DEG) return null;
  // Dragging RIGHT reveals what is behind on the left: the previous item.
  return dx > 0 ? 'prev' : 'next';
}

/**
 * Is this element inside a subtree that owns horizontal gestures?
 * Defensive about `closest` because a touch target can be a text node's parent in odd
 * cases, and a throw here would break navigation rather than merely skip an exemption.
 */
export function isSwipeExempt(el, selectors = SWIPE_EXEMPT) {
  let node = el;
  if (node && node.nodeType === 3) node = node.parentElement;
  if (!node || typeof node.closest !== 'function') return false;
  return selectors.some((sel) => {
    try { return node.closest(sel) !== null; } catch { return false; }
  });
}

/**
 * Did the element (or an ancestor) actually consume horizontal scroll during the drag?
 * A wide table already scrolled sideways under the finger has answered the gesture, and
 * navigating as well would be a second, unasked-for response to one movement.
 */
export function consumedHorizontalScroll(el) {
  let node = el && el.nodeType === 3 ? el.parentElement : el;
  while (node && node !== document.body) {
    const w = node.scrollWidth || 0;
    const c = node.clientWidth || 0;
    if (w > c + 1 && (node.scrollLeft || 0) > 0) return true;
    node = node.parentElement;
  }
  return false;
}
